import _ from 'lodash';

/**
 * CSV parsing and recipient bookkeeping for the notification composer.
 *
 * The notification service never sees the file — the dashboard parses it and
 * sends `userEmails` (and/or `userIds`) on `sendNotificationToUsers`. Admins
 * export these sheets from Excel, Google Sheets and Retool, and all three
 * differ, so the parsing here is deliberately forgiving about shape and strict
 * about what it will actually submit.
 *
 * Strictness matters more than it looks: input validation on that mutation
 * throws rather than returning, and the handler replaces the message, so one
 * malformed address comes back as "Failed to schedule notification! Please try
 * again later." with the real reason only in the server logs. Bad rows are
 * rejected here, named by line number, rather than failing the whole send with
 * an unactionable error.
 */

/** A Mongo ObjectId, as the notification service stores recipient ids. */
const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

/**
 * Deliberately simple. It is a pre-submit guard against sheet debris (stray
 * names, "N/A", trailing commentary), not an RFC 5322 implementation —
 * the backend resolves addresses against real users anyway.
 */
const EMAIL_REGEX = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;

export const isUserId = value =>
  OBJECT_ID_REGEX.test(String(value ?? '').trim());

export const isEmailLike = value =>
  EMAIL_REGEX.test(String(value ?? '').trim());

/**
 * Splits CSV text into rows of cells.
 *
 * Handles the three things the exports actually do differently: a UTF-8 BOM
 * from Excel (U+FEFF, which corrupts the first cell if left in place), CRLF
 * line endings, and quoted cells containing commas or newlines ("Doe, Jane").
 */
export const parseCsv = text => {
  const source = String(text ?? '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];

    if (inQuotes) {
      if (char === '"') {
        // A doubled quote inside a quoted cell is a literal quote.
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',' || char === ';' || char === '\t') {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      // Consume the \n of a CRLF pair so it does not open an empty row.
      if (char === '\r' && source[index + 1] === '\n') index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  row.push(cell);
  rows.push(row);

  return rows;
};

/** Trims a cell and strips quotes an export left around its value. */
const cleanCell = cell =>
  String(cell ?? '')
    .trim()
    .replace(/^["']+|["']+$/g, '')
    .trim();

/**
 * Turns raw CSV text into the two lists the mutation accepts.
 *
 * Two strategies, in order:
 *
 *   1. If the first row names an email or id column, only those columns are
 *      read. This is what makes an arbitrary export usable — a sheet with
 *      thirty columns of profile data has exactly one that matters, and
 *      reading the rest risks picking up a colleague's address out of a
 *      "referred by" column or an order id out of an id-shaped one.
 *   2. Otherwise every cell is examined and kept if it looks like an address
 *      or an id, which is what a headerless paste-into-a-sheet looks like.
 *
 * Emails come back lowercased and de-duplicated. The backend normalises them
 * too, but doing it here makes the recipient count shown to the admin match
 * what is actually sent.
 */
const HEADER_LABELS = [
  // The header the downloadable template ships with — without it, a template
  // filled in and re-uploaded reported its own header as a skipped row.
  'recipient',
  'recipients',
  'email',
  'emails',
  'email address',
  'e-mail',
  'user',
  'user id',
  'userid',
  'user ids',
  'id',
  'ids',
  'name',
  'first name',
  'last name',
  'full name',
  'username',
];

const EMAIL_HEADERS = [
  'email',
  'emails',
  'e-mail',
  'email address',
  'emailaddress',
  'email_address',
  'user email',
  'useremail',
  'recipient',
  'recipients',
];

const ID_HEADERS = [
  'id',
  'ids',
  '_id',
  'user id',
  'userid',
  'user_id',
  'user ids',
];

/**
 * A header that is about an address but not about a *property* of one.
 * "Email Verified" and "Email Preference" are columns of booleans and enums;
 * reading them would report every row as invalid.
 */
const EMAIL_FUZZY = /e-?mail/;
const EMAIL_FUZZY_EXCLUDE =
  /verif|confirm|status|valid|subscrib|prefer|opt[\s_-]?(in|out)|bounce|sent/;

/**
 * Deliberately strict: only a header that says it is a *user* id. A loose
 * match on "id" would happily pick up "Order ID" or "Transaction ID", and
 * those are Mongo ids too — they would pass validation and address the wrong
 * people, which is the one failure mode this file exists to prevent.
 */
const ID_FUZZY = /(^|[\s_-])user[\s_-]*ids?([\s_-]|$)/;

const findColumn = (headerCells, exactLabels, fuzzy, fuzzyExclude) => {
  const normalized = headerCells.map(cell => cleanCell(cell).toLowerCase());

  const exact = normalized.findIndex(header => exactLabels.includes(header));
  if (exact !== -1) return exact;

  return normalized.findIndex(
    header =>
      header &&
      fuzzy.test(header) &&
      !(fuzzyExclude && fuzzyExclude.test(header)),
  );
};

export const extractRecipientsFromCsv = text => {
  const emails = [];
  const ids = [];
  const invalid = [];
  const seenEmails = {};
  const seenIds = {};

  const addEmail = email => {
    if (!seenEmails[email]) {
      seenEmails[email] = true;
      emails.push(email);
    }
  };
  const addId = id => {
    if (!seenIds[id]) {
      seenIds[id] = true;
      ids.push(id);
    }
  };

  const rows = parseCsv(text);
  const header = rows[0] || [];
  const emailColumn = findColumn(
    header,
    EMAIL_HEADERS,
    EMAIL_FUZZY,
    EMAIL_FUZZY_EXCLUDE,
  );
  const idColumn = findColumn(header, ID_HEADERS, ID_FUZZY);

  // Column mode. Anything outside the named columns is ignored outright, so a
  // wide export needs no trimming down before it is uploaded.
  if (emailColumn !== -1 || idColumn !== -1) {
    rows.slice(1).forEach((cells, index) => {
      const line = index + 2;
      const values = [
        idColumn === -1 ? '' : cleanCell(cells[idColumn]),
        emailColumn === -1 ? '' : cleanCell(cells[emailColumn]),
      ].filter(Boolean);

      // A blank line, or a row with nothing in either column, is not an error.
      if (!values.length) return;

      // Which named column a value sits in does not decide what it is — the
      // downloadable template puts addresses and ids under one "Recipient"
      // heading, and a hand-made sheet often does the same. The column choice
      // says *where to look*; the value's own shape says what it is.
      const rowIds = values.filter(isUserId);
      const rowEmails = values.filter(isEmailLike);
      const rejected = values.filter(
        value => !isUserId(value) && !isEmailLike(value),
      );

      rejected.forEach(value => invalid.push({line, value}));

      // One row is one recipient, and an id identifies the user outright, so
      // a row carrying both contributes only the id. Counting both would show
      // twice the recipients the service is going to notify.
      if (rowIds.length) {
        rowIds.forEach(id => addId(id.toLowerCase()));
        return;
      }
      rowEmails.forEach(email => addEmail(email.toLowerCase()));
    });

    return {emails, ids, invalid};
  }

  // No usable header: fall back to reading every cell by its shape, which is
  // what a bare column of addresses pasted into a sheet looks like.
  rows.forEach((cells, rowIndex) => {
    const rowEmails = [];
    const rowIds = [];
    const leftovers = [];

    cells.forEach(rawCell => {
      const value = cleanCell(rawCell);
      if (!value) return;
      if (isEmailLike(value)) return rowEmails.push(value.toLowerCase());
      if (isUserId(value)) return rowIds.push(value.toLowerCase());
      leftovers.push(value);
    });

    // Same one-row-one-recipient rule as above.
    (rowIds.length ? [] : rowEmails).forEach(addEmail);
    rowIds.forEach(addId);

    if (rowEmails.length || rowIds.length || !leftovers.length) return;

    // A header row is expected debris, not something to warn about.
    if (
      rowIndex === 0 &&
      leftovers.every(value => HEADER_LABELS.includes(value.toLowerCase()))
    ) {
      return;
    }

    invalid.push({line: rowIndex + 1, value: leftovers.join(', ')});
  });

  return {emails, ids, invalid};
};

/** The id carried by a picker option key, "firstName;lastName;id;email". */
export const idFromOptionKey = optionKey =>
  String(optionKey ?? '').split(';')[2];

/**
 * How the admin is naming recipients. The two are mutually exclusive: the
 * picker resolves people by name, the bulk list names them by something the
 * backend can look up directly, and showing both at once invited a send where
 * half the recipients were in a field the admin had stopped looking at.
 */
export const RECIPIENT_MODES = {SEARCH: 'SEARCH', BULK: 'BULK'};

export const emptyRecipients = () => ({
  /** Which of the two inputs is in use. */
  mode: RECIPIENT_MODES.SEARCH,
  /** Option keys from the user picker, "firstName;lastName;id;email". */
  users: [],
  /**
   * Addresses and user ids added in bulk — pasted or read from a CSV.
   *
   * One list rather than two: an entry is classified by its own shape at
   * submit time, so the admin can paste emails and ids together and remove
   * either kind from the same set of chips.
   */
  entries: [],
  fileName: '',
  invalidRows: [],
});

/**
 * Cleans one pasted or uploaded value, returning null if it is neither an
 * address nor a user id.
 *
 * Names are rejected on purpose. The picker above resolves people by name
 * already; this field exists for the two things the backend can look up
 * directly, and silently accepting "Jane Doe" here would produce a recipient
 * that never resolves and is never reported as missing.
 */
export const normalizeEntry = raw => {
  const value = String(raw ?? '')
    .trim()
    .replace(/^["']+|["']+$/g, '')
    .trim();
  if (!value) return null;
  if (isEmailLike(value) || isUserId(value)) return value.toLowerCase();
  return null;
};

/**
 * The `{userIds, userEmails}` half of a `sendNotificationToUsers` payload.
 *
 * The backend takes both at once and notifies a user named twice only once, so
 * the picker's selections and an uploaded sheet simply union. Emails already
 * covered by a selected user are dropped here anyway, so the count the admin
 * confirms against is the count of distinct people.
 *
 * Only the resolved ids are persisted on the notification — `userEmails` names
 * recipients, it is not stored state — so reopening a saved notification shows
 * users rather than the uploaded addresses.
 */
export const buildRecipientPayload = (recipients = emptyRecipients()) => {
  // Only the active mode contributes. The other side's data is kept so that
  // switching back and forth does not destroy work, but it is never sent —
  // what the admin can see is exactly what goes out.
  if (recipients.mode === RECIPIENT_MODES.BULK) {
    const entries = (recipients.entries || [])
      .map(normalizeEntry)
      .filter(Boolean);

    const userIds = _.uniq(entries.filter(isUserId));
    const userEmails = _.uniq(entries.filter(isEmailLike));

    return {
      ...(userIds.length && {userIds}),
      ...(userEmails.length && {userEmails}),
    };
  }

  const userIds = _.uniq(
    (recipients.users || []).map(idFromOptionKey).filter(Boolean),
  );

  return {...(userIds.length && {userIds})};
};

/**
 * How many distinct people a send will reach, for the confirmation the admin
 * sees before submitting.
 *
 * It is a client-side count by necessity: the response does not report how
 * many addresses resolved, and unknown ones are dropped silently, so a typo'd
 * column reaches fewer people than this number without any warning.
 */
export const countRecipients = (recipients = emptyRecipients()) => {
  const {userIds = [], userEmails = []} = buildRecipientPayload(recipients);
  return userIds.length + userEmails.length;
};
